import { CommonModule, NgClass } from '@angular/common';
import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, forwardRef, Input, EventEmitter, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import flatpickr from 'flatpickr';
import { Instance } from 'flatpickr/dist/types/instance';
import { Options } from 'flatpickr/dist/types/options';

@Component({
    selector: 'app-date-time-picker',
    templateUrl: './date-time-picker.component.html',
    styleUrls: ['./date-time-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateTimePickerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class DateTimePickerComponent implements AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor {
  @ViewChild('dateInput') dateInput!: ElementRef;
  
  @Input() placeholder: string = 'Select...';
  @Input() mode: 'date' | 'datetime' | 'range' | 'time' = 'date'; 
  @Input() customConfig: Options = {}; 
  @Input() cssClass: string = ''; 
  @Input() minDate: Date | string | undefined;
  @Input() maxDate: Date | string | undefined;
  @Input() isDisabled: boolean = false;
  @Input() use24HourTime: boolean = false;
  
  @Output() dateSelected = new EventEmitter<string>();

  baseClasses = 'block w-full border border-gray-300 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-blue_Color focus:border-blue_Color';

  private flatpickrInstance!: Instance;
  private value: string = '';

  onChange: any = () => {};
  onTouch: any = () => {};

  ngAfterViewInit(): void {
    this.initFlatpickr();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.flatpickrInstance) {
      if (changes['minDate']) {
        this.flatpickrInstance.set('minDate', this.minDate);
      }
      if (changes['maxDate']) {
        this.flatpickrInstance.set('maxDate', this.maxDate);
      }
      if (changes['isDisabled']) {
        this.setDisabledState(this.isDisabled);
      }
      if (changes['mode'] || changes['customConfig']) {
        this.initFlatpickr();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }
  }

  private initFlatpickr(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }

    const modeConfig = this.getModeConfig();
    const finalConfig: Options = {
      ...modeConfig,
      ...this.customConfig,
      minDate: this.minDate,
      maxDate: this.maxDate,
      onChange: (selectedDates, dateStr) => {
        this.value = dateStr;
        this.onChange(dateStr);
        this.onTouch();
        // Emit the latest value to the parent component
        this.dateSelected.emit(dateStr); 
      }
    };

    this.flatpickrInstance = flatpickr(this.dateInput.nativeElement, finalConfig);

    // Sync disabled state on init
    this.setDisabledState(this.isDisabled);

    if (this.value) {
      this.flatpickrInstance.setDate(this.value);
    }
  }

  private getModeConfig(): Options {
    // Get the current time to use as the default for the time pickers
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    switch (this.mode) {
      case 'range':
        return { 
          mode: 'range', // Tells flatpickr to allow selecting a start and end date
          enableTime: false, 
          noCalendar: false, 
          dateFormat: 'Y-m-d', 
          altInput: true,  
          altFormat: 'F j, Y'  // User sees: "April 30, 2026 to May 5, 2026"
        };
      case 'datetime':
        return { 
          enableTime: true, 
          noCalendar: false, 
          minuteIncrement: 1, // Increment mins by 1 for more precise time selection
          dateFormat: 'Y-m-d\\TH:i', 
          time_24hr: this.use24HourTime , 
          altInput: true,  
          // altFormat: 'F j, Y at H:i', 
          altFormat: this.use24HourTime ? 'F j, Y at H:i' : 'F j, Y at h:i K',
          defaultHour: currentHour,
          defaultMinute: currentMinute,  

          // Optional: If you want to ensure the time updates to the EXACT minute 
          // they open the dropdown (in case they left the page open for hours)
          onOpen: function(selectedDates, dateStr, instance) {
            if (selectedDates.length === 0) {
              const exactNow = new Date();
              instance.config.defaultHour = exactNow.getHours();
              instance.config.defaultMinute = exactNow.getMinutes();
            }
          }
        };
      case 'time':
        return { 
          enableTime: true, 
          noCalendar: true, 
          minuteIncrement: 1, // Increment mins by 1 for more precise time selection
          // dateFormat: 'H:i', 
          dateFormat:  this.use24HourTime ? 'H:i' : 'h:i K',
          time_24hr: this.use24HourTime ,
          defaultHour: currentHour,
          defaultMinute: currentMinute,
        };
      case 'date':
      default:
        return { 
          enableTime: false, 
          noCalendar: false, 
          dateFormat: 'Y-m-d',
          altInput: true,  
          altFormat: 'F j, Y',  
        };
    }
  }

  writeValue(value: any): void {
    this.value = value;
    if (this.flatpickrInstance && value) {
      this.flatpickrInstance.setDate(value);
    } else if (this.flatpickrInstance && !value) {
      this.flatpickrInstance.clear();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  
  setDisabledState(isDisabled: boolean): void {
  this.isDisabled = isDisabled; 
  
  if (this.flatpickrInstance) {
    const input = this.flatpickrInstance.altInput || this.flatpickrInstance.input;
    
    if (isDisabled) {
      input.setAttribute('disabled', 'disabled');
    } else {
      input.removeAttribute('disabled');
    }

    // Force the UI update regardless of Angular's change detection
    input.classList.toggle('bg-gray-100', isDisabled);
    input.classList.toggle('opacity-50', isDisabled);
    input.classList.toggle('cursor-not-allowed', isDisabled);
    
    input.classList.toggle('bg-white', !isDisabled);
    input.classList.toggle('cursor-pointer', !isDisabled);
  }
}
}